import { flattenColumns } from './flattenColumns';

/**
 * Recursively updates the visibility, groupIndex, sortIndex, and sortOrder
 * of `columns` based on the currently visible columns from the grid.
 *
 * @param {Array} columns - Columns to update (structured/nested)
 * @param {Array} visibleColumns - Currently visible columns from grid (nested)
 */
export function syncVisibility(columns, visibleColumns) {
    if (!Array.isArray(columns) || !Array.isArray(visibleColumns)) return;

    // Flatten visibleColumns to simplify visibility lookup
    const flatVisible = flattenColumns(visibleColumns);

    updateColumnVisibility(columns, flatVisible);
}

/**
 * Recursively update visibility and grouping/sorting info for each column
 *
 * @param {Array} columns - Structured/nested columns
 * @param {Array} flatVisibleColumns - Flattened array of visible columns
 */
function updateColumnVisibility(columns, flatVisibleColumns) {
    columns.forEach((col) => {
        if (col.columns && Array.isArray(col.columns)) {
            // Band column: sync its own visibleIndex so drag-reordered band headers export in screen order
            const bandIdx = flatVisibleColumns.findIndex(
                (x) => !x.dataField && x.caption === col.caption
            );
            if (bandIdx > -1) {
                col.visibleIndex = flatVisibleColumns[bandIdx].visibleIndex;
            }
            // Recurse into children
            updateColumnVisibility(col.columns, flatVisibleColumns);
        } else {
            // Find matching visible column
            let index;
            if (col.cellTemplate) {
                index = flatVisibleColumns.findIndex(
                    (x) => x.dataField === col.dataField && x.cellTemplate === col.cellTemplate
                );
            } else {
                index = flatVisibleColumns.findIndex(
                    (x) => x.dataField === col.dataField && !x.cellTemplate
                );
            }

            // Set visibility — always false when not in visible set, regardless of original definition
            col.visible = index > -1;

            // Always sync visibleIndex from screen state so drag-drop column order is preserved in export
            if (index > -1) {
                col.visibleIndex = flatVisibleColumns[index].visibleIndex;
            }

            // Update groupIndex, sortIndex, sortOrder
            const idx = flatVisibleColumns.findIndex(
                (x) =>
                    x.dataField === col.dataField &&
                    x.name === col.name &&
                    x.groupIndex !== undefined
            );
            if (idx > -1) {
                col.groupIndex = flatVisibleColumns[idx].groupIndex;
                col.sortIndex = flatVisibleColumns[idx].sortIndex;
                col.sortOrder = flatVisibleColumns[idx].sortOrder;
            } else {
                col.groupIndex = undefined;
            }
        }
    });
}
